import { parseJwt } from './jwtDecoder';
import { ENDPOINTS } from './appConfig';

const KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  IS_AUTHENTICATED: 'isAuthenticated',
  USERNAME: 'username',
  ROLE: 'role',
  KEEP_SIGNED_IN: 'keepSignedIn',
  CHAT_SESSION_ID: 'chat_session_id',
  DOMAIN: 'domain',
  TENANT_ID: 'tenant_id',
  IS_BOOTSTRAP_ADMIN: 'is_bootstrap_admin',
};

const KEYS_ARR = Object.values(KEYS);

function primaryStorage() {
  return localStorage.getItem(KEYS.KEEP_SIGNED_IN) === 'true' ? localStorage : sessionStorage;
}

export function setAuthData(data, keepSignedIn) {
  const storage = keepSignedIn ? localStorage : sessionStorage;

  if (keepSignedIn) {
    KEYS_ARR.forEach(k => sessionStorage.removeItem(k));
    localStorage.setItem(KEYS.KEEP_SIGNED_IN, 'true');
  } else {
    KEYS_ARR.forEach(k => localStorage.removeItem(k));
    localStorage.removeItem(KEYS.KEEP_SIGNED_IN);
  }

  storage.setItem(KEYS.ACCESS_TOKEN, data.access);
  storage.setItem(KEYS.REFRESH_TOKEN, data.refresh);
  storage.setItem(KEYS.IS_AUTHENTICATED, 'true');
  if (data.username) storage.setItem(KEYS.USERNAME, data.username);
  if (data.role) storage.setItem(KEYS.ROLE, data.role);
  if (data.is_bootstrap_admin !== undefined) {
    storage.setItem(KEYS.IS_BOOTSTRAP_ADMIN, String(data.is_bootstrap_admin));
  }
  // Single domain string ('surveillance', 'construction', or 'public')
  const domainVal = data.domain || (Array.isArray(data.domains) ? data.domains[0] : '');
  if (domainVal) storage.setItem(KEYS.DOMAIN, domainVal);
  const tenantVal = data.tenant_id || data.tenant;
  if (tenantVal) storage.setItem(KEYS.TENANT_ID, tenantVal);
}

export function setItem(key, value) {
  primaryStorage().setItem(key, value);
}

export function getItem(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

export function removeItem(key) {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export const CHAT_SESSION_KEY_PREFIX = 'chat_session_id_';

export function clearAuthData() {
  KEYS_ARR.forEach(k => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
  Object.keys(localStorage)
    .filter(k => k.startsWith(CHAT_SESSION_KEY_PREFIX))
    .forEach(k => localStorage.removeItem(k));
}

export function hasValidAuth() {
  const token = getItem(KEYS.ACCESS_TOKEN);
  const isAuth = getItem(KEYS.IS_AUTHENTICATED);
  return !!(token && isAuth === 'true');
}

export function getKeepSignedIn() {
  return localStorage.getItem(KEYS.KEEP_SIGNED_IN) === 'true';
}

/**
 * Returns single domain for current user ('surveillance', 'construction', or 'public').
 */
export function getDomain() {
  return getItem(KEYS.DOMAIN) || '';
}

/**
 * Returns list containing single domain for backwards compatibility.
 */
export function getDomains() {
  const d = getDomain();
  return d ? [d] : [];
}

/**
 * Returns true if current user matches specified domain.
 */
export function hasDomain(domain) {
  return getDomain() === domain;
}

/**
 * Returns the stored role string (e.g. "admin", "operator").
 */
export function getRole() {
  return getItem(KEYS.ROLE) || '';
}

/**
 * Returns current tenant identifier string (e.g. "tenant_acme" or "acme").
 */
export function getTenantId() {
  const stored = getItem(KEYS.TENANT_ID);
  if (stored) return stored;
  const token = getItem(KEYS.ACCESS_TOKEN);
  if (token) {
    try {
      const payload = parseJwt(token);
      return payload?.tenant_id || payload?.tenant || '';
    } catch {
      return '';
    }
  }
  return '';
}

/**
 * Scopes issued by Django in the access token (e.g. "construction:write").
 * UI hint only — Django and the chat service enforce the same scopes server-side.
 */
export function getScopes() {
  const token = getItem(KEYS.ACCESS_TOKEN);
  if (!token) return [];
  const payload = parseJwt(token);
  return Array.isArray(payload?.scopes) ? payload.scopes : [];
}

export function hasScope(scope) {
  return getScopes().includes(scope);
}

export const SCOPES = {
  CONSTRUCTION_READ: 'construction:read',
  CONSTRUCTION_WRITE: 'construction:write',
  DOCUMENTS_WRITE: 'documents:write',
  CHAT_USE: 'chat:use',
  SURVEY_WRITE: 'survey:write',
  SURVEY_FLY: 'survey:fly',
};

/**
 * Returns true if the current user is the bootstrap admin (domain='public').
 */
export function isBootstrapAdmin() {
  return getItem(KEYS.IS_BOOTSTRAP_ADMIN) === 'true' || hasDomain('public');
}

/**
 * Backward compatibility alias for isBootstrapAdmin
 */
export function isRoot() {
  return isBootstrapAdmin();
}

export function logout() {
  fetch(ENDPOINTS.ASSET_SESSION, { method: 'DELETE', credentials: 'same-origin', keepalive: true }).catch(() => {});
  clearAuthData();
  window.location.href = '/login';
}

export { KEYS as AUTH_KEYS };

